import { XsI18nPipe } from '@nx-fe/pipes';
import { FeatureToggleService, GroupContextService, hd, TicketService } from '@nx-fe/services';

export type IExtendedTicketModel = hd.IExtendedTicketModel;
type IDropdownListItem = hd.IDropdownListItem;

export interface ITimeoutSchedule {
  promise: Promise<any>;
}

export interface TagModel {
  /** The unique tag ID */
  tagId?: number;
  /** The associated tag title */
  title: string;
  /** The associated tagGroup Id */
  tagGroupId?: number;
}
export class Ticket {
  /** The ticket ID */
  public ticketId: string; // Note: These are actually numbers for netCore
  /** The reporter ID */
  public reporterPublicId: string;
  /** The assignee ID */
  public assigneePublicId?: string;
  /** The creation user ID */
  public creationUserPublicId?: string;
  /** The resolution user ID */
  public resolutionUserPublicId?: string;
  /** The reporter Name */
  public reporterName: string;
  /** The title of the ticket */
  public title: string;
  /** Tags list */
  public associatedTags: TagModel[] = [];
  /** Should hide extra tags */
  public hideTags = true;
  /** Should update tags list */
  public update: { toggle: boolean } = { toggle: false };
  /** The status of the ticket */
  public status: string;
  /** The status of the ticket */
  public statusCaption: string;
  /** The status icon of the ticket */
  public statusGlyphIcon: string;
  /** The priority text of the ticket */
  public priorityCaption: string;
  /** The priority icon of the ticket */
  public priorityGlyphIcon: string;
  /** The assignee of the ticket */
  public assignee: string;
  /** The method by which the ticket was reported */
  public reportedMethodGlyphIcon: string;
  /** The creation date/time of the ticket */
  public creationDate: string;
  /** The creation user of the ticket */
  public creationUser: string;
  /** The last updated date/time of the ticket */
  public updateDate: string;
  /** The group name */
  public groupName: string;
  /** List of filtered fields */
  public filteredFields: string;
  /** Due date for the ticket */
  public dueDate: string;
  /** Short group name for the ticket */
  public ShortGroupName: string;


  private timeout: ITimeoutSchedule;

  constructor(
    ticketModel: IExtendedTicketModel,
    ticketService: TicketService,
    timeout: ITimeoutSchedule,
    groupContextService: GroupContextService,
    $filter: XsI18nPipe,
    featureToggleService: FeatureToggleService
  ) {
    this.timeout = timeout;
    this.ticketId = ticketModel.ticketId;
    this.reporterPublicId = ticketModel.reporterPublicId;
    this.assigneePublicId = ticketModel.assigneePublicId;
    this.creationUserPublicId = ticketModel.creationUserPublicId;
    this.resolutionUserPublicId = ticketModel.resolutionUserPublicId;

    if (ticketModel.reporterName) {
      this.reporterName = ticketModel.reporterName;
    }
    this.title = ticketModel.title;
    this.status = ticketModel.status;
    const ticketStatus = ticketService.getTicketStatus(ticketModel.status) as IDropdownListItem;
    if (ticketStatus) {
      this.statusCaption = ticketStatus.caption;
      this.statusGlyphIcon = ticketStatus.glyphicon;
    }

    if ((ticketModel.filteredFields && ticketModel.filteredFields.toUpperCase().includes('PRIORITY') || !ticketModel.priority)) {
      this.priorityCaption = $filter.transform('N/A');
      this.priorityGlyphIcon = '';
    } else {
      const ticketPriority = ticketService.getTicketPriority(ticketModel.priority) as IDropdownListItem;
      if (ticketPriority) {
        this.priorityCaption = ticketPriority.caption;
        this.priorityGlyphIcon = ticketPriority.glyphicon;
      }
    }

    this.assignee = ticketModel.assignee;
    const ticketReporterMethod = ticketService.getTicketReportedMethod(ticketModel.reportedMethod) as IDropdownListItem;
    if (ticketReporterMethod) {
      this.reportedMethodGlyphIcon = ticketReporterMethod.glyphicon;
    }
    this.creationDate = ticketModel.creationDate;
    this.creationUser = ticketModel.creationUser;
    this.updateDate = ticketModel.updateDate;
    this.dueDate = ticketModel.dueDate && ticketModel.status !== 'resolved' ? ticketModel.dueDate : '';

    if (ticketModel.groupName) {
      this.groupName = ticketModel.groupName;
    } else {
      if (ticketModel.groupId) {
        this.groupName = groupContextService.getGroupName(ticketModel.groupId);
      }
    }
    this.ShortGroupName = ticketModel.ShortGroupName;

    if (featureToggleService.isEnabled('Groups')) {
      this.filteredFields = ticketModel.filteredFields;
    }
  }

  /** Indicates whether the ticket has a reporter */
  public hasReporter = (): boolean => !!(this.reporterName && this.reporterName.length > 0);

  /** Indicates whether the ticket has an assignee */
  public hasAssignee = (): boolean => !!(this.assignee && this.assignee.length > 0);

  /** Indicates whether the ticket has a title */
  public hasTitle = (): boolean => !!(this.title && this.title.length > 0);

  /** Indicates whether the ticket has all tags loaded */
  public allTagsLoaded = (): boolean => !!(this.associatedTags && this.associatedTags.length > 0);

  /** Indicates whether the ticket has a resolution target date */
  public hasDueDate = (): boolean => !!(this.dueDate && this.dueDate.length > 0);

  /** Indicates whether the ticket is overdue */
  public isOverdue = (): boolean => !!(this.hasDueDate() && new Date() > new Date(this.dueDate) && this.status !== 'resolved');

  /** Indicates whether the ticket is due today */
  public isDueToday = (): boolean => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
    const dueDate = new Date(this.dueDate);
    return !!(
      this.hasDueDate() &&
      !this.isOverdue() &&
      today <= dueDate &&
      dueDate < tomorrow &&
      this.status !== 'resolved'
    );
  };

  public tagPostLink = () => {
    if (!this.timeout.promise) {
      //this.timeout.promise = new Promise();
    } else {
      //this.$timeout.cancel(this.timeout.promise);
      //this.timeout.promise = this.$timeout();
    }
  };
}
